'use client';

import Link from 'next/link';
import { Bell, Eye, Heart, Star, ThumbsUp } from 'lucide-react';
import { trackSectionCta } from '@/components/GoogleAnalytics';

const BENEFITS = [
  {
    icon: Eye,
    title: 'View Profiles',
    note: '(Unlimited)',
    body: 'Browse thousands of Marathi profiles, completely free, with no limit.',
  },
  {
    icon: Heart,
    title: 'Send Interest',
    note: '(Unlimited*)',
    body: 'Express interest with a single tap - always free.',
  },
  {
    icon: Star,
    title: 'Shortlist Profiles',
    note: '(Unlimited)',
    body: 'Save your favourites and revisit them anytime.',
  },
  {
    icon: ThumbsUp,
    title: 'Like Profiles',
    note: '(Unlimited)',
    body: 'Mutual likes are notified to you instantly on the app.',
  },
  {
    icon: Bell,
    title: 'Daily Recommendations',
    note: '(Unlimited)',
    body: 'Fresh, preferences matched profiles delivered every day.',
  },
] as const;

function RegisterButton({ className }: { className?: string }) {
  return (
    <Link
      href="/register"
      onClick={() =>
        trackSectionCta('click_register', 'homepage_free_section', 'Register Now in 2 minutes')
      }
      className={`inline-flex items-center justify-center rounded-full bg-primary-600 px-7 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-primary-700 md:text-[16px] ${className ?? ''}`}
    >
      Register Now in 2 minutes
    </Link>
  );
}

export default function HomeFreeSection() {
  return (
    <section
      className="bg-white bg-repeat font-sans"
      style={{ backgroundImage: "url('/assets/img/moroccan-bg.png')" }}
    >
      <div className="mx-auto grid w-full max-w-[1120px] items-center gap-8 px-5 py-16 md:px-8 lg:grid-cols-[minmax(280px,0.85fr)_minmax(0,1.15fr)] lg:gap-16 lg:py-20">
        <div className="text-center lg:text-left">
          <h2>
            <span className="inline-flex h-[80px] items-end font-sans tracking-[-0.04em] text-black md:h-[120px]">
              <span className="text-[80px] font-black leading-[80px] md:text-[120px] md:leading-[120px]">100</span>
              <span className="flex h-[80px] items-end text-[40px] font-bold leading-[80px] md:h-[120px] md:text-[56px] md:leading-[120px]">%</span>
            </span>
            <span className="mt-2 block text-[26px] font-bold leading-tight text-black md:mt-3 md:text-[32px]">
              Free. Actually free.
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-[340px] text-[15px] leading-relaxed text-neutral-700 lg:mx-0 lg:max-w-[380px] lg:text-[16px]">
            No hidden paywall between you and your match. Everything on the right is yours from day one.
          </p>
          <RegisterButton className="mt-8 hidden lg:inline-flex" />
        </div>

        <ul className="flex flex-col gap-3">
          {BENEFITS.map(({ icon: Icon, title, note, body }) => (
            <li
              key={title}
              className="flex items-start gap-4 rounded-[18px] bg-white p-[21px] shadow-[0_8px_24px_rgba(0,0,0,0.06)] md:px-[25px]"
            >
              <Icon className="mt-0.5 h-6 w-6 shrink-0 text-black" strokeWidth={1.75} aria-hidden />
              <div className="min-w-0 text-left">
                <p className="text-[16px] font-semibold leading-snug text-black md:text-[18px]">
                  {title} <span className="text-[14px] font-normal">{note}</span>
                </p>
                <p className="mt-[7px] text-[14px] leading-snug text-neutral-600">{body}</p>
              </div>
            </li>
          ))}
        </ul>

        <RegisterButton className="justify-self-center lg:hidden" />
      </div>
    </section>
  );
}
